import { inject, LOCALE_ID } from '@angular/core';
import { BLOG_SLUGS } from './blog-slugs.generated';
/**
 * Gemeinsame SEO-Bausteine für Seiten ohne eigenen Helfer (Blog, Startseite):
 * Canonical, hreflang-Alternates und JSON-LD. Werden serverseitig gesetzt
 * (SSR/Prerender), damit Crawler sie im HTML sehen.
 */

export const SITE = 'https://marktbox.de';
export type SiteLocale = 'de' | 'da' | 'nl';

/** LOCALE_ID ('de', 'da-DK', 'nl-NL' …) → URL-Präfix. */
export function localePrefix(locale: string): SiteLocale {
  return locale.startsWith('da') ? 'da' : locale.startsWith('nl') ? 'nl' : 'de';
}

/** Absolute URL zu einem Pfad ('/de/blog/x' oder '/assets/…'). */
export function absUrl(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${SITE}/${path.replace(/^\//, '')}`;
}

export function setCanonical(document: Document, href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel=canonical]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

/**
 * Setzt hreflang-Alternates (inkl. x-default → Deutsch). Pfade ohne Domain,
 * z. B. { de: '/de/blog/x', da: '/da/blog/y' }. Entfernt vorher gesetzte.
 */
export function setHreflang(document: Document, alternates: Partial<Record<SiteLocale, string>>): void {
  clearHreflang(document);
  const entries = Object.entries(alternates) as [SiteLocale, string][];
  if (entries.length < 2) return; // ohne Sprachpendant kein hreflang
  const add = (lang: string, path: string) => {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = lang;
    link.href = absUrl(path);
    link.setAttribute('data-seo-hreflang', '');
    document.head.appendChild(link);
  };
  for (const [lang, path] of entries) add(lang, path);
  if (alternates.de) add('x-default', alternates.de);
}

export function clearHreflang(document: Document): void {
  document.querySelectorAll('link[data-seo-hreflang]').forEach((l) => l.remove());
}

export function injectJsonLd(document: Document, id: string, obj: object): void {
  document.getElementById(id)?.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
}

export function removeJsonLd(document: Document, id: string): void {
  document.getElementById(id)?.remove();
}

/**
 * Blog-Slug der aktuellen Sprache zu einem deutschen Slug. Nur in einem
 * Injection-Kontext aufrufen (z. B. Feld-Initialisierer einer Komponente).
 */
export function localBlogSlug(deSlug: string): string {
  const locale = localePrefix(inject(LOCALE_ID));
  return locale === 'de' ? deSlug : (BLOG_SLUGS[deSlug]?.[locale] ?? deSlug);
}
